import type {
  InternalNetworkDto,
  UpdateInternalNetworkDto,
} from '@terra/shared-contracts-internal-network';
import {
  getBrowserTimezone,
  type AutomationSchedule,
} from '../../automation/automationScheduleShared';
import {
  DEFAULT_SCANNING_SCHEDULE,
  type ScanningConfigurationChange,
} from '../../settings/automation/ScanningConfigurationCard';

export type InternalNetworkExecutionTier = InternalNetworkDto['executionTier'];

export type InternalNetworkEdrAssessmentMode =
  InternalNetworkDto['edrAssessmentMode'];

export type InternalNetworkSchedule = NonNullable<
  InternalNetworkDto['schedule']
>;

export type InternalNetworkExecutionPersistRequest = {
  updates: UpdateInternalNetworkDto;
  showSuccessToast: boolean;
};

export const EXECUTION_TIER_VALUES = [
  'VALIDATION_ONLY',
  'CONTROLLED_AD',
  'FULL_LATERAL_MOVEMENT',
] as const satisfies readonly InternalNetworkExecutionTier[];

export const EDR_ASSESSMENT_MODE_VALUES = [
  'EFFECTIVENESS',
  'COVERAGE',
] as const satisfies readonly InternalNetworkEdrAssessmentMode[];

export const DEFAULT_EDR_ASSESSMENT_MODE: InternalNetworkEdrAssessmentMode =
  'EFFECTIVENESS';

export const isInternalNetworkExecutionTier = (
  value: string,
): value is InternalNetworkExecutionTier =>
  (EXECUTION_TIER_VALUES as readonly string[]).includes(value);

export const isInternalNetworkEdrAssessmentMode = (
  value: string,
): value is InternalNetworkEdrAssessmentMode =>
  (EDR_ASSESSMENT_MODE_VALUES as readonly string[]).includes(value);

export const resolveInternalNetworkEdrAssessmentMode = (
  mode: InternalNetworkEdrAssessmentMode | null | undefined,
): InternalNetworkEdrAssessmentMode => mode ?? DEFAULT_EDR_ASSESSMENT_MODE;

export const toInternalNetworkSchedule = (
  schedule: AutomationSchedule,
): InternalNetworkSchedule => ({
  daysOfWeek: schedule.daysOfWeek,
  startHour: schedule.startHour,
  endHour: schedule.endHour,
  timezone: schedule.timezone ?? getBrowserTimezone(),
});

const toLimitedSchedule = ({
  limitScheduleEnabled,
  automationSchedule,
}: {
  limitScheduleEnabled: boolean;
  automationSchedule?: AutomationSchedule;
}): InternalNetworkSchedule | null =>
  limitScheduleEnabled
    ? toInternalNetworkSchedule(automationSchedule ?? DEFAULT_SCANNING_SCHEDULE)
    : null;

export const resolveDiscoveryScheduleChange = (
  change: ScanningConfigurationChange,
): InternalNetworkExecutionPersistRequest | null => {
  if (typeof change.limitScheduleEnabled === 'boolean') {
    return {
      updates: {
        schedule: toLimitedSchedule({
          limitScheduleEnabled: change.limitScheduleEnabled,
          automationSchedule: change.automationSchedule,
        }),
      },
      showSuccessToast: true,
    };
  }

  if (change.automationSchedule) {
    return {
      updates: {
        schedule: toInternalNetworkSchedule(change.automationSchedule),
      },
      showSuccessToast: false,
    };
  }

  return null;
};

export const resolveScenarioExecutionChange = ({
  change,
  executionScheduleEnabled,
}: {
  change: ScanningConfigurationChange;
  executionScheduleEnabled: boolean;
}): InternalNetworkExecutionPersistRequest | null => {
  if (typeof change.smokeTestEnabled === 'boolean') {
    return {
      updates: { smokeTestEnabled: change.smokeTestEnabled },
      showSuccessToast: true,
    };
  }

  if (typeof change.limitScheduleEnabled === 'boolean') {
    return {
      updates: {
        executionSchedule: toLimitedSchedule({
          limitScheduleEnabled: change.limitScheduleEnabled,
          automationSchedule: change.automationSchedule,
        }),
      },
      showSuccessToast: true,
    };
  }

  if (change.automationSchedule && executionScheduleEnabled) {
    return {
      updates: {
        executionSchedule: toInternalNetworkSchedule(change.automationSchedule),
      },
      showSuccessToast: true,
    };
  }

  return null;
};
